import { useFieldProps } from "Components/Hooks";
import { Horizontal } from "Components/Horizontal";
import { WhatIsThisModal } from "Components/WhatIsThisModal";
import { useFormikContext } from "formik";
import { Checkbox, isChecked } from "Inputs/Checkbox";
import { RadioYesNo } from "Inputs/Radio";
import { Textarea } from "Inputs/Textarea";
import { Textbox } from "Inputs/Textbox";
import { ErrorMessage } from "Inputs/Tools";
import { Program } from "SharedConstants";
import React from "react";
import { range } from "Tools";

export interface FitnessInstructorProfession {
  ProfessionID: number;
  DisplayName: string;
}

function InstructorTypes(props: {
  ProfessionList: FitnessInstructorProfession[];
}) {
  const columnSize = Math.ceil(props.ProfessionList.length / 2);
  return (
    <div className="columns" style={{ width: "100%" }}>
      {range(0, props.ProfessionList.length, columnSize).map(i => (
        <div key={i} className="column">
          {props.ProfessionList.slice(i, i + columnSize).map(e => (
            <Checkbox
              key={e.ProfessionID}
              fieldName={`Profession_${e.ProfessionID}`}
              noVerticalSlop
            >
              {e.DisplayName}
            </Checkbox>
          ))}
        </div>
      ))}
    </div>
  );
}

function InstructorTypeAndActivities(props: {
  isSup: boolean;
  ProfessionList: FitnessInstructorProfession[];
}) {
  const otherInstructorCheckboxField = useFieldProps("OtherInstructorCheckbox");
  const { errors } = useFormikContext<any>();
  return (
    <>
      <Horizontal isRequired label="Instructor Type" shouldNotPadLabel>
        <div style={{ width: "100%" }}>
          <div style={{ marginBottom: "1em" }}>
            <em>
              Please check all that apply.
              {props.isSup && (
                <>
                  {" "}
                  Requirements apply – see next application page. SUP Rental
                  Operator coverage will be bound separately. We will contact
                  you with further information if you select this coverage.
                </>
              )}
            </em>
            <ErrorMessage>{errors["Profession_List"]}</ErrorMessage>
          </div>
          <InstructorTypes {...props} />
          {!props.isSup && (
            <div style={{ marginBottom: "0.75em" }}>
              <Checkbox fieldName={otherInstructorCheckboxField.name}>
                Other (200 hours minimum):{" "}
              </Checkbox>
            </div>
          )}
          {isChecked(otherInstructorCheckboxField.value) && (
            <Textbox
              fieldName="OtherInstructorType"
              placeholder="Type of Instruction"
            />
          )}
        </div>
      </Horizontal>
      <Horizontal label="Activities">
        <Textarea
          fieldName="DescriptionOfInstructorActivities"
          placeholder={
            props.isSup
              ? "Describe instructor's SUP activities..."
              : "Describe instructor's personal training activities..."
          }
        />
      </Horizontal>
    </>
  );
}

function OpenWaterQuestion(props: { premium: string }) {
  return (
    <Horizontal isRequired label="Open Water" shouldNotPadLabel>
      <RadioYesNo fieldName="IsOpenWater">
        <div>
          <div>
            <span className="has-text-weight-bold">
              Would you like to add coverage for Open Water (Ocean) Activities
              for an additional premium of {props.premium}?{" "}
            </span>
            <WhatIsThisModal modalTitle="Open Water">
              Open Water locations include: Oceans; Sea; Estuaries leading out
              to open water.
            </WhatIsThisModal>
          </div>
          <div className="is-italic">
            Note: For open water coverage, an accreditation certificate is
            required.
          </div>
        </div>
      </RadioYesNo>
    </Horizontal>
  );
}

export interface InstructorTypeSectionModelProps {
  ProfessionList: FitnessInstructorProfession[];
  YogaIDs: number[];
  IsInlineSkatingProducer: boolean;
  Program: Program;
  OpenWaterPremium: string;
}

export function InstructorTypeSection(props: InstructorTypeSectionModelProps) {
  const isSup = props.Program === Program.Sup;
  const { values } = useFormikContext<Record<string, unknown>>();
  const isYogaProfessionSelected = Object.entries(values).some(
    ([field, value]) =>
      props.YogaIDs.map(id => `Profession_${id}`).includes(field) &&
      value !== undefined &&
      value !== false,
  );
  return (
    <>
      {!props.IsInlineSkatingProducer && (
        <InstructorTypeAndActivities {...props} isSup={isSup} />
      )}
      <Horizontal isRequired label="Clients">
        <Textbox
          fieldName="EstimatedNumberOfClients"
          placeholder="Estimated number of clients annually"
        />
      </Horizontal>
      {isYogaProfessionSelected && (
        <Horizontal isRequired label="Yoga Experience" shouldNotPadLabel>
          <RadioYesNo fieldName="HasYogaExperience">
            <div className="has-text-weight-bold">
              Do you have at least 50 hours of yoga instructional experience?
            </div>
          </RadioYesNo>
        </Horizontal>
      )}
      {isSup && <OpenWaterQuestion premium={props.OpenWaterPremium} />}
    </>
  );
}
